import React from 'react';
import { Tab } from 'semantic-ui-react';
import Loader from 'components/Loader/Loader';
import { EnforcementData, ReportInterface } from 'types';
import { usdFormatterWithoutCents } from 'utils/helpers';
import MonthYearUtil from 'utils/monthYear';
import WidgetBox from '../widgets/WidgetBox';
import RevenueAndCars from './RevenueAndCars';
import LiveUpdateLabel from 'components/widgets/LiveUpdateLabel';
import { AuthorizedView, Roles } from 'components/Utilities/AuthorizedView';

type EnforcementWidgetProps = {
    report: ReportInterface | null;
    loading: boolean;
    updateReportsLoading: boolean;
};

function EnforcementWidget({ report, loading, updateReportsLoading }: EnforcementWidgetProps): JSX.Element {
    if (loading || !report) {
        return (
            <WidgetBox title="Enforcement Revenue">
                <Loader centered />
            </WidgetBox>
        );
    }

    const monthYearUtils = new MonthYearUtil();

    const { text: currentMonthYearText } = monthYearUtils.currentMonthYear; // This is something like `{ text: "April 2023" }`
    const [currentMonth, currentYear] = currentMonthYearText.split(' ');

    const hasTicketRevenue = !!report.has_ticket_revenue;

    function renderPane(data: EnforcementData): JSX.Element {
        const ticketRevenue = data.ticket_revenue ?? 0;
        const autostartRevenue = data.autostart_revenue ?? data.enforcement_revenue - ticketRevenue;
        return (
            <Tab.Pane attached={false}>
                <RevenueAndCars
                    revenue={usdFormatterWithoutCents(hasTicketRevenue ? autostartRevenue : data.enforcement_revenue)}
                    cars={data.rental_count.toLocaleString('en-US')}
                    violationPayments={hasTicketRevenue ? usdFormatterWithoutCents(ticketRevenue) : undefined}
                    violationsPaid={hasTicketRevenue ? (data.ticket_count ?? 0).toLocaleString('en-US') : undefined}
                />
            </Tab.Pane>
        );
    }

    const panes = [];

    if (report.enforcement_uplift.seven_days.enforcement_revenue > 0) {
        panes.push({
            menuItem: 'This Week',
            render: (): JSX.Element => renderPane(report.enforcement_uplift.seven_days),
        });
    }

    if (report.enforcement_uplift.month_to_date.enforcement_revenue > 0) {
        panes.push({
            menuItem: currentMonth,
            render: (): JSX.Element => renderPane(report.enforcement_uplift.month_to_date),
        });
    }

    if (report.enforcement_uplift.year_to_date.enforcement_revenue > 0) {
        panes.push({
            menuItem: currentYear,
            render: (): JSX.Element => renderPane(report.enforcement_uplift.year_to_date),
        });
    }

    return (
        <WidgetBox title="Enforcement Revenue" rightElement={<LiveUpdateLabel loading={updateReportsLoading} />}>
            <Tab menu={{ secondary: true }} panes={panes} />
        </WidgetBox>
    );
}

function EnforcementWidgetWrapper(props: EnforcementWidgetProps): JSX.Element {
    return (
        <AuthorizedView allowed={Roles.Admin}>
            <EnforcementWidget {...props} />
        </AuthorizedView>
    );
}

export default EnforcementWidgetWrapper;
